import React, {useCallback, useContext, useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useHttp} from '../hooks/http.hook'
import {AuthContext} from '../context/AuthContext'
import config from '../config.json'
import {
    getAuthHeader,
    getIcon,
    getItems,
    getLocalStorageItem,
    getPathAndQueryParams,
    objectLang,
    setErrorWindow,
    setImagesDataToSlider,
    setLocalStorageItem,
    setPathBack
} from "../functions"
import {LinksClass} from "../components/LinksClass"
import {ModalWin} from "../elements/ModalWin"
import {LoaderClass} from "../elements/LoaderClass"
import {PathLineClass} from "../elements/PathLineClass"

export const FolderPage = ({isPublic = false}) => {
    setLocalStorageItem('page', 'FolderPage')
    if (isPublic) setLocalStorageItem('page', 'FolderPublicPage')
    const auth = useContext(AuthContext)
    const {request, loading} = useHttp()
    const [documents, setDocuments] = useState({})
    const [items, setItems] = useState([])
    const [allowed, setAllowed] = useState(true)
    const [dataModal, setDataModal] = useState({})
    const [countItems, setCountItems] = useState(0)
    const navigate = useNavigate()
    let folderId = useParams().id
    if (auth.isAuthenticated) {
        if (!folderId || folderId === 'my-links' || folderId === '0') {
            const lang = objectLang().language
            folderId = auth.rootFolder
            navigate(`/${lang.lang}/folder/open/${auth.rootFolder}`, {replace: false})
            // window.location.replace(`/${lang.lang}/folder/open/${auth.rootFolder}`)
        }
    }

    let opening = 'open'
    if (isPublic) {
        opening = 'open-public'
    }

    let host
    window.location.host === 'localhost:3000' ? host = config.localHost : host = config.onrender

    const checkDevice = useCallback(async (openingDevice) => {
        try {
            const isUpdate = await request(`${host}/api/folder/check-content/${folderId}?openingDevice=${openingDevice}`, 'GET')
            if (getLocalStorageItem(folderId)) {
                const cached = getLocalStorageItem(folderId)
                if (isUpdate['result'] && isUpdate['openingDeviceDB'] === openingDevice) {
                    setDocuments(cached)
                    setItems(getItems(cached))
                    setCountItems(
                        cached.folders.length + cached.links.length + cached.notes.length
                    )
                } else {
                    getDocsFolder().then()
                }
            }
        } catch (e) {
            console.log(e.messages)
        }
    }, [request, host, auth, folderId])

    const getDocsFolder = useCallback(async () => {
        const lang = objectLang().language
        const l = lang.lang
        let deviceId = crypto.randomUUID()
        if (auth && auth.deviceId) deviceId = auth.deviceId
        try {
            const data = await request(
                `${host}/api/folder/${opening}/${folderId}?openingDevice=${deviceId}`,
                'GET',
                null,
                {
                    Authorization: getAuthHeader(auth)
                })

            if (data.code === 542 && data.accessLink) {
                setAllowed(false)
                const url = new URL(data.accessLink)
                const pathCode = url.pathname.split('folder')
                let path = `/${l}/`
                if (pathCode.length > 0) {
                    path = pathCode[1]
                }
                navigate(`/${l}/folder${path}${url.search}${url.hash}`, {replace: true})
            }

            if (data.status === 403) {
                setAllowed(false)
                setDataModal(setErrorWindow('', data.code))
                return
            }

            if (data.code === 541) {
                setAllowed(false)
                setDataModal(setErrorWindow(
                    lang.modal.errors.title,
                    data.code,
                    lang.messages.code_541,
                    lang.modal.buttons.btnOK,
                    `replaceTo /${l}/`,
                    lang.modal.buttons.btnCancel,
                    `replaceTo ${getPathAndQueryParams()}`
                ))
            } else if (data.status > 200) {
                setAllowed(false)
                setDataModal(setErrorWindow(
                    '',
                    data.code,
                    '',
                    lang.modal.buttons.btnOK,
                    `replaceTo /${l}/`,
                    lang.modal.buttons.btnCancel,
                    `replaceTo ${getPathAndQueryParams()}`
                ))
            } else {
                setPathBack(getPathAndQueryParams())
            }

            const res = {
                owner: data.owner,
                name: data.name,
                parent: folderId,
                folders: data.folders,
                links: data.links,
                notes: data.notes,
                path: data.path,
                upLevel: data.upLevel,
                opening: opening,
                isOwner: data.isOwner,
                sort: data.sort,
                order: data.order,
                openingDevice: deviceId
            }

            if (data['newAccessToken']) auth.accessToken = data['newAccessToken']
            setDocuments(res)
            setItems([])
            setCountItems(res.folders.length + res.links.length + res.notes.length)
            setLocalStorageItem(folderId, res)

        } catch (e) {
            setAllowed(false)
            setDataModal(setErrorWindow('', 541))
        }
    }, [host, auth, folderId, request, opening, navigate])

    useEffect(() => {
        if (getLocalStorageItem(folderId)) {
            const cached = getLocalStorageItem(folderId)
            checkDevice(cached.openingDevice).then()
        } else {
            getDocsFolder().then()
        }
    }, [getDocsFolder, folderId, isPublic, checkDevice])

    useEffect(() => {
        if (!items.length) return

        const enriched = items.map(item => {
            if (item.type === 'link') {
                return {
                    ...item,
                    from: item.from || '',
                    thumbUrl: item.thumbUrl || ''
                }
            }
            return item
        })

        setImagesDataToSlider(enriched)
    }, [items])

    if (!allowed) {
        return <ModalWin data={dataModal}/>
    }

    if (Object.keys(documents).length > 0) {
        if (items.length === 0) {
            setItems(getItems(documents))
            setLocalStorageItem(`sort-${folderId}`, documents.sort)
            setLocalStorageItem(`order-${folderId}`, documents.order)
        }

        for (const item of items) {
            if (item.type === 'link') {
                if (!item.thumbUrl) {
                    const iconDefault = require('../images/link-logo-default.png')
                    item.thumbUrl = getIcon(item.from, iconDefault) || iconDefault
                }
            }
        }

        return (
            <div style={{position: 'relative'}}>
                <PathLineClass documents={documents}/>

                {loading && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'top',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.4)',
                        zIndex: 10
                    }}>
                        <LoaderClass position={'fixed'} top={120} left={'calc(50% - 24px)'}/>
                    </div>
                )}

                <LinksClass
                    auth={auth}
                    documents={documents}
                    items={items}
                    rootFolder={auth.rootFolder}
                    setCountItems={setCountItems}
                    countItems={countItems}
                    navigate={navigate}
                />
            </div>
        )
    }

    return (
        <>
            {loading && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    top: '75px',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.4)',
                    zIndex: 10
                }}>
                    <LoaderClass position={'fixed'} top={120} left={'calc(50% - 24px)'}/>
                </div>
            )}
        </>

    )
}
